import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CreditCard, Mail, Lock, ArrowRight, AlertCircle, Eye, EyeOff, TrendingUp, Zap, ShieldCheck } from "lucide-react";
import { loginMerchant } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

function Counter({ to, suffix = "" }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !done.current) {
        done.current = true;
        let s = 0; const step = (to / 1600) * 16;
        const t = setInterval(() => { s += step; if (s >= to) { setV(to); clearInterval(t); } else setV(Math.floor(s)); }, 16);
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{v.toLocaleString()}{suffix}</span>;
}

export default function MerchantLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError(t("pleaseEnterBothEmailAndPassword")); return; }
    setLoading(true);
    try {
      await loginMerchant(email, password);
      navigate("/MerchantDashboard");
    } catch (err) {
      setError(err.message || t("loginFailed"));
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'Inter', -apple-system, sans-serif", background: "#060F0E", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

        /* === LEFT PANEL === */
        .ml-left {
          flex: 1; position: relative;
          display: flex; flex-direction: column; justify-content: space-between;
          padding: 44px 52px;
          background: linear-gradient(160deg, #071A10 0%, #081E12 50%, #060F0E 100%);
          border-right: 1px solid rgba(16,185,129,0.1);
          overflow: hidden;
        }

        .ml-left-glow-a {
          position: absolute; border-radius: 50%; pointer-events: none;
          width: 600px; height: 600px; top: -200px; left: -200px;
          background: radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 65%);
          animation: ml-breathe 7s ease-in-out infinite;
        }

        .ml-left-glow-b {
          position: absolute; border-radius: 50%; pointer-events: none;
          width: 450px; height: 450px; bottom: -150px; right: -100px;
          background: radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 65%);
          animation: ml-breathe 9s ease-in-out infinite 2s;
        }

        @keyframes ml-breathe {
          0%,100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.08); opacity: 1; }
        }

        /* Stat cards */
        .ml-stat {
          padding: 18px 20px; border-radius: 14px;
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(16,185,129,0.1);
          cursor: default;
          transition: all 0.28s cubic-bezier(0.4,0,0.2,1);
        }
        .ml-stat:hover {
          background: rgba(16,185,129,0.05);
          border-color: rgba(16,185,129,0.22);
          transform: translateY(-3px);
          box-shadow: 0 8px 28px rgba(16,185,129,0.1);
        }

        /* === RIGHT PANEL === */
        .ml-right {
          width: 480px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          padding: 40px 44px;
          background: #060F0E;
        }

        /* Inputs - DARK GREEN */
        .ml-input {
          width: 100%; height: 52px;
          background: rgba(4,30,18,0.9) !important;
          border: 1.5px solid rgba(16,185,129,0.15) !important;
          border-radius: 12px;
          color: #D1FAE5 !important;
          font-size: 14px; font-family: 'Inter', sans-serif;
          padding: 0 46px !important;
          outline: none !important;
          transition: all 0.25s ease;
          -webkit-appearance: none;
          caret-color: #10B981;
        }
        .ml-input::placeholder { color: rgba(110,231,183,0.2) !important; }
        .ml-input:hover { border-color: rgba(16,185,129,0.3) !important; background: rgba(4,35,20,0.95) !important; }
        .ml-input:focus {
          border-color: rgba(16,185,129,0.6) !important;
          background: rgba(5,40,24,0.95) !important;
          box-shadow: 0 0 0 4px rgba(16,185,129,0.1), 0 2px 16px rgba(16,185,129,0.07) !important;
        }

        .ml-input-icon { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); pointer-events: none; transition: color 0.25s; }

        .ml-eye { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(110,231,183,0.25); padding: 4px; border-radius: 6px; display: flex; transition: all 0.2s; }
        .ml-eye:hover { color: #34D399; background: rgba(16,185,129,0.1); }

        /* Button */
        .ml-btn {
          width: 100%; height: 52px;
          background: linear-gradient(135deg, #065F46 0%, #059669 45%, #10B981 100%);
          border: none; border-radius: 12px;
          color: white; font-size: 15px; font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 4px 22px rgba(16,185,129,0.32), inset 0 1px 0 rgba(255,255,255,0.1);
          position: relative; overflow: hidden;
        }
        .ml-btn-shine { position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg,transparent,rgba(255,255,255,0.1),transparent); transform: skewX(-20deg); transition: left 0.55s ease; }
        .ml-btn:hover:not(:disabled) .ml-btn-shine { left: 150%; }
        .ml-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(16,185,129,0.45); background: linear-gradient(135deg,#047857 0%,#10B981 50%,#34D399 100%); }
        .ml-btn:active:not(:disabled) { transform: translateY(0); }
        .ml-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .ml-spin { width: 19px; height: 19px; border: 2.5px solid rgba(255,255,255,0.25); border-top-color: white; border-radius: 50%; animation: ml-rot 0.65s linear infinite; }
        @keyframes ml-rot { to { transform: rotate(360deg); } }

        .ml-card {
          background: linear-gradient(155deg, rgba(8,22,16,0.97) 0%, rgba(4,12,9,0.99) 100%);
          border: 1px solid rgba(16,185,129,0.13);
          border-radius: 22px; padding: 40px;
          box-shadow: 0 0 0 1px rgba(16,185,129,0.04), 0 36px 90px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.04);
          position: relative; overflow: hidden; width: 100%;
        }
        .ml-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg,transparent 0%,rgba(16,185,129,0.55) 35%,rgba(6,182,212,0.45) 65%,transparent 100%); }

        .ml-label { font-size: 11.5px; font-weight: 600; color: rgba(110,231,183,0.38); letter-spacing: 0.07em; text-transform: uppercase; margin-bottom: 8px; display: block; font-family: 'Inter', sans-serif; }
        .ml-link { color: #34D399; text-decoration: none; font-size: 13px; font-weight: 500; transition: color 0.2s; }
        .ml-link:hover { color: #6EE7B7; }
      `}</style>

      {/* Left panel */}
      <div className="ml-left">
        <div className="ml-left-glow-a" />
        <div className="ml-left-glow-b" />

        {/* Logo */}
        <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <motion.div whileHover={{ scale: 1.08, rotate: -6 }} transition={{ type: "spring", stiffness: 380 }}
              style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#047857,#10B981)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 18px rgba(16,185,129,0.4)" }}>
              <CreditCard size={18} color="white" />
            </motion.div>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 20, color: "#ECFDF5", letterSpacing: "-0.025em" }}>1401</span>
          </div>
        </motion.div>

        {/* Hero */}
        <motion.div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", paddingTop: 36 }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.12 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: 100, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.18)", marginBottom: 22, width: "fit-content" }}>
            <ShieldCheck size={12} color="#34D399" />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#34D399", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase" }}>Merchant Portal</span>
          </div>

          <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: 40, fontWeight: 800, color: "#ECFDF5", letterSpacing: "-0.04em", lineHeight: 1.15, margin: "0 0 16px" }}>
            Accept payments<br />
            <span style={{ background: "linear-gradient(135deg,#10B981,#06B6D4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>everywhere.</span>
          </h1>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 14.5, color: "rgba(110,231,183,0.38)", lineHeight: 1.7, maxWidth: 340, margin: 0 }}>
            Manage your payment gateway, view transactions, and grow your business from one unified dashboard.
          </p>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginTop: 36 }}>
            {[
              { label: "Merchants", value: 2400, suffix: "+", icon: TrendingUp, color: "#10B981" },
              { label: "Volume/mo", value: 8, suffix: "M+", icon: CreditCard, color: "#06B6D4" },
              { label: "Uptime", value: 99, suffix: ".9%", icon: Zap, color: "#F59E0B" },
            ].map((s, i) => (
              <motion.div key={i} className="ml-stat" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 + i * 0.09 }}>
                <s.icon size={15} style={{ color: s.color, marginBottom: 8 }} />
                <div style={{ fontFamily: "monospace", fontSize: 21, fontWeight: 700, color: s.color, lineHeight: 1 }}>
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "rgba(110,231,183,0.3)", marginTop: 5, fontWeight: 500 }}>{s.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Certs */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {["PCI DSS", "ISO 27001", "GDPR"].map((tag, i) => (
              <span key={i} style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(16,185,129,0.1)", fontFamily: "monospace", fontSize: 10.5, color: "rgba(110,231,183,0.3)", fontWeight: 500 }}>{tag}</span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right form */}
      <div className="ml-right">
        <motion.div style={{ width: "100%" }} initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, ease: [0.22,1,0.36,1] }}>
          <div className="ml-card">
            <div style={{ marginBottom: 30 }}>
              <motion.div whileHover={{ scale: 1.07, rotate: -5 }} transition={{ type: "spring", stiffness: 380, damping: 14 }}
                style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#065F46,#10B981)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: "0 8px 26px rgba(16,185,129,0.38), 0 0 0 1px rgba(16,185,129,0.2)", cursor: "default" }}>
                <CreditCard size={22} color="white" />
              </motion.div>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 700, color: "#ECFDF5", margin: 0, letterSpacing: "-0.025em" }}>{t("welcomeBack")}</h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13.5, color: "rgba(110,231,183,0.32)", margin: "5px 0 0" }}>Sign in to your merchant account</p>
            </div>

            <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} style={{ overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", borderRadius: 11, background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.15)", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: 13 }}>
                      <AlertCircle size={14} style={{ flexShrink: 0, color: "#F87171" }} />
                      {error}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="ml-label">{t("emailAddress")}</label>
                <div style={{ position: "relative" }}>
                  <Mail size={16} className="ml-input-icon" style={{ color: focused === "email" ? "#10B981" : "rgba(110,231,183,0.2)" }} />
                  <input type="email" className="ml-input" placeholder="merchant@example.com" value={email} onChange={e => setEmail(e.target.value)} onFocus={() => setFocused("email")} onBlur={() => setFocused("")} required />
                </div>
              </div>

              <div>
                <label className="ml-label">{t("password")}</label>
                <div style={{ position: "relative" }}>
                  <Lock size={16} className="ml-input-icon" style={{ color: focused === "pass" ? "#10B981" : "rgba(110,231,183,0.2)" }} />
                  <input type={showPw ? "text" : "password"} className="ml-input" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setFocused("pass")} onBlur={() => setFocused("")} style={{ paddingRight: 46 }} required />
                  <button type="button" className="ml-eye" onClick={() => setShowPw(!showPw)}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(110,231,183,0.35)" }}>
                  <input type="checkbox" style={{ accentColor: "#10B981" }} />
                  {t("rememberMe")}
                </label>
                <a href="#" className="ml-link">{t("forgotPassword")}</a>
              </div>

              <motion.button type="submit" className="ml-btn" disabled={loading} whileTap={!loading ? { scale: 0.98 } : {}} style={{ marginTop: 4 }}>
                <div className="ml-btn-shine" />
                {loading ? (<><div className="ml-spin" />{t("signingIn")}</>) : (<>{t("signIn")}<ArrowRight size={15} /></>)}
              </motion.button>
            </form>

            <div style={{ height: 1, background: "rgba(16,185,129,0.08)", margin: "26px 0 18px" }} />
            <p style={{ textAlign: "center", fontFamily: "'Inter', sans-serif", fontSize: 13, color: "rgba(110,231,183,0.25)" }}>
              {t("needHelpContact")} <a href="mailto:support@1401.com" className="ml-link">support@1401.com</a>
            </p>
          </div>
          <p style={{ textAlign: "center", fontFamily: "'Inter', sans-serif", fontSize: 12, color: "rgba(110,231,183,0.12)", marginTop: 16 }}>© {new Date().getFullYear()} 1401 · All rights reserved</p>
        </motion.div>
      </div>
    </div>
  );
}